/**
 * useBundleHandlers - Hook für Bundle-Kauf Logik
 *
 * Extrahiert aus ShopWithTimer.js für bessere Wartbarkeit.
 * Verwaltet Seal Bundle und Joker Bundle Interaktionen.
 */

import { useState, useCallback } from 'react';
import soundService from '../soundService';

/**
 * @param {Object} config - Konfiguration
 * @param {Object} config.currentBundle - Aktuelles Seal Bundle
 * @param {Object} config.currentJokerBundle - Aktuelles Joker Bundle
 * @param {number} config.currentPlayerCoins - Münzen des Spielers
 * @param {Array} config.currentPlayerStash - Siegel-Stash des Spielers
 * @param {Array} config.playerJokers - Joker des Spielers
 * @param {function} config.onBuyBundle - Callback für Bundle-Kauf
 * @param {function} config.onBuyJokerBundle - Callback für Joker-Bundle-Kauf
 * @param {boolean} config.isLocalTestMode - Test-Modus aktiv
 * @param {number} config.currentPlayer - Aktueller Spieler-Index
 */
export function useBundleHandlers({
  currentBundle,
  currentJokerBundle,
  currentPlayerCoins,
  currentPlayerStash,
  playerJokers,
  onBuyBundle,
  onBuyJokerBundle,
  isLocalTestMode,
  currentPlayer,
}) {
  // Seal Bundle state
  const [openedBundle, setOpenedBundle] = useState(null);
  const [selectedBundleSeals, setSelectedBundleSeals] = useState([]);

  // Joker Bundle state
  const [openedJokerBundle, setOpenedJokerBundle] = useState(null);
  const [selectedJokerIndices, setSelectedJokerIndices] = useState([]);

  // Info modal state
  const [infoModalBundle, setInfoModalBundle] = useState(null);

  const stashSlotsFree = 3 - (currentPlayerStash?.length || 0);

  // === Seal Bundle Handlers ===

  const handleBuyBundle = useCallback(() => {
    if (!currentBundle) return;
    if (currentPlayerCoins < currentBundle.finalPrice) return;

    // Check if player has enough stash slots for the required picks
    const requiredPicks = currentBundle.pickCount || 1;
    if (stashSlotsFree < requiredPicks) {
      soundService.play('shop_cant_afford');
      return;
    }

    soundService.play('booster_open');
    setOpenedBundle(currentBundle);
    setSelectedBundleSeals([]);
  }, [currentBundle, currentPlayerCoins, stashSlotsFree]);

  // Get the required number of picks for the opened bundle
  const getMaxPicks = useCallback(() => {
    if (!openedBundle) return 1;
    // We only open bundles when stashSlotsFree >= pickCount, so just return pickCount
    return openedBundle.pickCount || 1;
  }, [openedBundle]);

  const handleToggleBundleSeal = useCallback((sealIndex) => {
    const maxPicks = getMaxPicks();
    setSelectedBundleSeals(prev => {
      if (prev.includes(sealIndex)) {
        // Deselect
        soundService.play('card_deselect');
        return prev.filter(i => i !== sealIndex);
      } else {
        // Select only if there's room
        if (prev.length < maxPicks) {
          soundService.play('card_select');
          return [...prev, sealIndex];
        }
        soundService.play('shop_cant_afford');
        return prev;
      }
    });
  }, [getMaxPicks]);

  const handleConfirmBundleSeals = useCallback(() => {
    if (!openedBundle || selectedBundleSeals.length === 0) return;

    // Require exact pickCount (or max available if stash is limited)
    const requiredPicks = getMaxPicks();
    if (selectedBundleSeals.length !== requiredPicks) {
      soundService.play('shop_cant_afford');
      return;
    }

    const selectedSeals = selectedBundleSeals.map(idx => openedBundle.seals[idx]);
    soundService.play('shop_buy_seal');
    onBuyBundle?.(selectedSeals, isLocalTestMode ? currentPlayer : null);
    setOpenedBundle(null);
    setSelectedBundleSeals([]);
  }, [openedBundle, selectedBundleSeals, onBuyBundle, isLocalTestMode, currentPlayer, getMaxPicks]);

  const handleCloseBundleOverlay = useCallback(() => {
    setOpenedBundle(null);
    setSelectedBundleSeals([]);
  }, []);

  // === Joker Bundle Handlers ===

  const jokerMaxCount = 3;
  const jokerSlotsFree = jokerMaxCount - (playerJokers?.length || 0);

  const handleOpenJokerBundle = useCallback(() => {
    if (!currentJokerBundle || currentJokerBundle.purchased) return;
    if (currentPlayerCoins < currentJokerBundle.finalPrice) return;

    // Check if player has enough joker slots for the required picks
    const requiredPicks = currentJokerBundle.pickCount || 1;
    if (jokerSlotsFree < requiredPicks) {
      soundService.play('shop_cant_afford');
      return;
    }

    soundService.play('booster_open');
    setOpenedJokerBundle(currentJokerBundle);
    setSelectedJokerIndices([]);
  }, [currentJokerBundle, currentPlayerCoins, jokerSlotsFree]);

  // Get the required number of picks for the opened joker bundle
  const getJokerMaxPicks = useCallback(() => {
    if (!openedJokerBundle) return 1;
    return openedJokerBundle.pickCount || 1;
  }, [openedJokerBundle]);

  const handleToggleJokerFromBundle = useCallback((jokerIndex) => {
    const maxPicks = getJokerMaxPicks();
    setSelectedJokerIndices(prev => {
      if (prev.includes(jokerIndex)) {
        // Deselect
        soundService.play('card_deselect');
        return prev.filter(i => i !== jokerIndex);
      } else {
        // Select only if there's room
        if (prev.length < maxPicks) {
          soundService.play('card_select');
          return [...prev, jokerIndex];
        }
        soundService.play('shop_cant_afford');
        return prev;
      }
    });
  }, [getJokerMaxPicks]);

  const handleConfirmJokerBundle = useCallback(() => {
    if (!openedJokerBundle || selectedJokerIndices.length === 0) return;

    // Require exact pickCount
    const requiredPicks = getJokerMaxPicks();
    if (selectedJokerIndices.length !== requiredPicks) {
      soundService.play('shop_cant_afford');
      return;
    }

    const selectedJokerIds = selectedJokerIndices.map(idx => openedJokerBundle.jokers[idx].id);
    soundService.play('shop_buy_joker');
    onBuyJokerBundle?.(selectedJokerIds, isLocalTestMode ? currentPlayer : null);
    setOpenedJokerBundle(null);
    setSelectedJokerIndices([]);
  }, [openedJokerBundle, selectedJokerIndices, onBuyJokerBundle, isLocalTestMode, currentPlayer, getJokerMaxPicks]);

  const handleCloseJokerBundleOverlay = useCallback(() => {
    setOpenedJokerBundle(null);
    setSelectedJokerIndices([]);
  }, []);

  // === Info Modal Handler ===

  const handleBundleInfoClick = useCallback((bundle, bundleType) => {
    setInfoModalBundle({ bundle, type: bundleType });
  }, []);

  const closeInfoModal = useCallback(() => {
    setInfoModalBundle(null);
  }, []);

  // Reset state when player changes (test mode)
  const resetBundleState = useCallback(() => {
    setOpenedBundle(null);
    setSelectedBundleSeals([]);
    setOpenedJokerBundle(null);
    setSelectedJokerIndices([]);
    setInfoModalBundle(null);
  }, []);

  // Compute maxPicks for current bundles
  const maxPicks = getMaxPicks();
  const jokerMaxPicks = getJokerMaxPicks();

  return {
    // Seal Bundle
    openedBundle,
    selectedBundleSeals,
    maxPicks,  // How many seals player should pick
    handleBuyBundle,
    handleToggleBundleSeal,
    handleConfirmBundleSeals,
    handleCloseBundleOverlay,

    // Joker Bundle
    openedJokerBundle,
    selectedJokerIndices,
    jokerMaxPicks,  // How many jokers player should pick
    handleOpenJokerBundle,
    handleToggleJokerFromBundle,
    handleConfirmJokerBundle,
    handleCloseJokerBundleOverlay,

    // Info Modal
    infoModalBundle,
    handleBundleInfoClick,
    closeInfoModal,

    // Utility
    resetBundleState,
    stashSlotsFree,
  };
}

export default useBundleHandlers;
